import { Shield, Zap } from 'lucide-react'
import { ENTITY_ICON_COMPONENTS } from '@/components/neo4j/cytoscape/entity-icons'
import type { TopEntityEntry } from '@/services/api/graph'
import { ScoreChip } from '../primitives/ScoreChip'

export interface LeaderboardRowProps {
  row: TopEntityEntry
  rank: number
  entityType: string
  onClick?: () => void
}

function MetricChip({ label, value }: { label: string; value: string | number }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md px-1.5 h-5 text-3xs font-medium bg-[var(--muted)]/60 text-[var(--muted-foreground)]">
      <span className="opacity-70">{label}</span>
      <span className="text-[var(--foreground)] tabular-nums">{value}</span>
    </span>
  )
}

export function LeaderboardRow({ row, rank, entityType, onClick }: LeaderboardRowProps) {
  const Icon = ENTITY_ICON_COMPONENTS[entityType]
  const displayName = row.name || `${entityType} #${row.id?.slice(-6) ?? '?'}`
  return (
    <button
      type="button"
      onClick={onClick}
      className="grid grid-cols-[28px_18px_1fr_auto_auto_auto] items-center gap-2 w-full px-3 py-2 rounded-md hover:bg-[var(--muted)]/40 cursor-pointer border-b border-[var(--border)]/40 last:border-b-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
    >
      <span className="text-xs font-semibold tabular-nums text-[var(--muted-foreground)]">
        #{rank}
      </span>
      {Icon ? (
        <Icon className="size-3.5 text-[var(--muted-foreground)]" />
      ) : (
        <span className="size-3.5" />
      )}
      <span className="text-sm text-[var(--foreground)] truncate">{displayName}</span>
      <ScoreChip value={row.score} />
      <div className="hidden lg:flex items-center gap-1">
        {row.degree != null && <MetricChip label="deg" value={row.degree} />}
        {row.betweenness != null && <MetricChip label="btw" value={row.betweenness.toFixed(2)} />}
        {row.source_count != null && row.source_count > 0 && (
          <MetricChip label="src" value={row.source_count} />
        )}
      </div>
      <div className="flex items-center gap-1 text-[var(--muted-foreground)] max-md:w-11">
        {row.is_suspect === true && (
          <span
            title="Suspect"
            className="inline-flex items-center justify-center size-5 rounded-md bg-[var(--destructive)]/10 text-[var(--destructive)]"
          >
            <Shield className="size-3" />
          </span>
        )}
        {row.is_bridge === true && (
          <span
            title="Bridge"
            className="inline-flex items-center justify-center size-5 rounded-md bg-[var(--chart-1)]/15 text-[var(--chart-1)]"
          >
            <Zap className="size-3" />
          </span>
        )}
      </div>
    </button>
  )
}
